import * as Sentry from "@sentry/react";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { App } from "./App";
import "./index.css";
import "./pages/BrokerWorkspace/access";
import { workspacePrivateEndpoints } from "./pages/BrokerWorkspace/privacy";
import {
  scrubReportEvent,
  scrubReportBreadcrumb,
  omitReportRecording,
  isRenewalDraftLocation,
} from "./lib/reportTelemetry";
import { config } from "./lib/config";

Sentry.init({
  dsn: "https://c009a111bb61595c99e69f9380aab435@o4509634254077952.ingest.de.sentry.io/4509634255716432",
  sendDefaultPii: true,
  _experiments: {
    enableLogs: true,
    beforeSendLog: (log) =>
      omitReportRecording(log, window.location.pathname) ? null : log,
  },

  enabled:
    config.VITE_ENV !== "development" &&
    window.location.pathname !== "/broker-workspace" &&
    !isRenewalDraftLocation(window.location.pathname),

  beforeSend: (event) => scrubReportEvent(event, window.location.pathname),
  beforeSendTransaction: (event) =>
    scrubReportEvent(event, window.location.pathname),
  beforeBreadcrumb: (breadcrumb) =>
    scrubReportBreadcrumb(breadcrumb, window.location.pathname),

  integrations: [
    Sentry.consoleLoggingIntegration({
      levels: ["log", "error", "warn"],
    }),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: config.VITE_ENV === "production",
      maskAllInputs: config.VITE_ENV === "production",
      blockAllMedia: config.VITE_ENV === "production",
      networkDetailAllowUrls: [config.VITE_API_URL],
      networkCaptureBodies: true,
      networkDetailDenyUrls: workspacePrivateEndpoints,
      beforeAddRecordingEvent: (event) =>
        omitReportRecording(event, window.location.pathname) ? null : event,
    }),
  ],

  environment: config.VITE_ENV,

  // TODO: Change this to 0.1 in production when we setup the subscription
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
});

const enableMocks = async () => {
  if (import.meta.env.VITE_USE_MOCKS !== "true") return;
  const { worker } = await import("./mocks/browser");
  await worker.start({
    onUnhandledRequest: "bypass",
    serviceWorker: { url: "/mockServiceWorker.js" },
  });
};

enableMocks().then(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StrictMode>,
  );
});
